import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { itineraryApi } from '../services/api';
import { ArrowRight, ArrowLeft, Loader2, Mail, Lock, Eye, EyeOff, User, CheckCircle } from 'lucide-react';
import { COUNTRIES_DATA } from '../components/trip-creation/tripConstants';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  TravelerStep,
  NationalityStep,
  OriginStep,
  DestinationStep,
  DateStep,
  BudgetStep,
  StyleStep,
  InterestsStep,
  PreferencesStep,
  StepProgressBar,
} from '../components/trip-creation';

const NewTripPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, login, register, loginWithGoogle } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showOtherNationality, setShowOtherNationality] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editItineraryId, setEditItineraryId] = useState(null);
  const [tripReady, setTripReady] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authForm, setAuthForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  
  const [formData, setFormData] = useState({
    traveler_type: '',
    adults: 1,
    kids: 0,
    nationality: '',
    origin_country: '',
    origin_city: '',
    destination_city: '',
    destination_cities: [],
    destination_country: '',
    destination_countries: [],
    start_date: '',
    end_date: '',
    budget: '',
    travel_styles: [],
    interests: [],
    preferences: [],
    custom_preferences: '',
  });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [templateDuration, setTemplateDuration] = useState(null);

  // Check for pre-selected destination from landing page or edit mode
  // Runs once on mount to read sessionStorage — dependencies intentionally omitted
  useEffect(() => {
    const savedDestination = sessionStorage.getItem('selectedDestination');
    const editTrip = sessionStorage.getItem('editTrip');
    const templateTrip = sessionStorage.getItem('templateTrip');
    
    if (editTrip) {
      try {
        const { id, trip_input } = JSON.parse(editTrip);
        setIsEditMode(true);
        setEditItineraryId(id);
        setFormData({
          ...trip_input,
          destination_cities: trip_input.destination_cities || [trip_input.destination_city],
        });
        if (trip_input.start_date) setStartDate(new Date(trip_input.start_date));
        if (trip_input.end_date) setEndDate(new Date(trip_input.end_date));
        sessionStorage.removeItem('editTrip');
      } catch { /* silent */ }
    } else if (templateTrip) {
      try {
        const trip_input = JSON.parse(templateTrip);
        setFormData(prev => ({
          ...prev,
          destination_city: trip_input.destination_city || '',
          destination_country: trip_input.destination_country || '',
          destination_cities: trip_input.destination_cities || [trip_input.destination_city],
          traveler_type: trip_input.traveler_type || prev.traveler_type,
          budget: trip_input.budget || prev.budget,
          travel_styles: trip_input.travel_styles || prev.travel_styles,
          interests: trip_input.interests || prev.interests,
          adults: trip_input.adults || prev.adults,
          kids: trip_input.kids || 0,
        }));
        if (trip_input.duration_days) setTemplateDuration(trip_input.duration_days);
        sessionStorage.removeItem('templateTrip');
      } catch { /* silent */ }
    } else if (savedDestination) {
      try {
        const { city, country } = JSON.parse(savedDestination);
        setFormData(prev => ({
          ...prev,
          destination_city: city,
          destination_cities: [city],
          destination_country: country,
        }));
        sessionStorage.removeItem('selectedDestination');
      } catch { /* silent */ }
    }
  }, []);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (country) => {
    updateFormData('destination_country', country);
    if (!formData.destination_countries.includes(country)) {
      updateFormData('destination_countries', [...formData.destination_countries, country]);
    }
    updateFormData('destination_city', '');
    updateFormData('destination_cities', []);
  };

  const removeCountry = (country) => {
    const newCountries = formData.destination_countries.filter(c => c !== country);
    const newCities = formData.destination_cities.filter(city => {
      const countryForCity = Object.entries(COUNTRIES_DATA).find(([, cities]) => cities.includes(city));
      return countryForCity && countryForCity[0] !== country;
    });
    setFormData(prev => ({
      ...prev,
      destination_countries: newCountries,
      destination_country: newCountries[newCountries.length - 1] || '',
      destination_cities: newCities,
      destination_city: newCities[0] || '',
    }));
  };

  const toggleCitySelection = (city) => {
    setFormData((prev) => {
      const cities = prev.destination_cities || [];
      if (cities.includes(city)) {
        const newCities = cities.filter(c => c !== city);
        return { ...prev, destination_cities: newCities, destination_city: newCities[0] || '' };
      }
      const newCities = [...cities, city];
      return { ...prev, destination_cities: newCities, destination_city: newCities[0] };
    });
  };

  const toggleArrayItem = (field, value) => {
    setFormData((prev) => {
      const arr = prev[field];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter((v) => v !== value) };
      }
      return { ...prev, [field]: [...arr, value] };
    });
  };

  const handleNationalityChange = (nationality) => {
    if (nationality === 'other') {
      setShowOtherNationality(true);
      updateFormData('nationality', '');
    } else {
      setShowOtherNationality(false);
      updateFormData('nationality', nationality);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.traveler_type && formData.adults > 0;
      case 2: return formData.nationality && formData.nationality.trim() !== '';
      case 3: return formData.origin_country && formData.origin_city;
      case 4: return (formData.destination_cities.length > 0 || formData.destination_city) && formData.destination_country;
      case 5: return formData.start_date && formData.end_date;
      case 6: return formData.budget;
      case 7: return formData.travel_styles.length > 0;
      case 8: return formData.interests.length > 0;
      case 9: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 9) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(-1);
      setTimeout(() => window.scrollTo(0, 0), 50);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const _doGenerate = async () => {
    setLoading(true);
    try {
      if (isEditMode && editItineraryId) {
        await itineraryApi.updateTripInput(editItineraryId, formData);
        toast.success('Trip updated! You can now regenerate the itinerary.');
        navigate(`/itinerary/${editItineraryId}`);
      } else {
        const response = await itineraryApi.create(formData);
        toast.success('Trip created! Redirecting to itinerary...');
        navigate(`/itinerary/${response.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save trip');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Trip planning is complete — mark trip as ready
    setTripReady(true);
    toast.success('Trip details saved! Now generate your itinerary.');
  };

  const handleGenerate = async () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('pendingTrip', JSON.stringify(formData));
      setShowAuthModal(true);
      return;
    }
    await _doGenerate();
  };

  // After auth completes in the modal, generate the itinerary
  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    toast.success('Account created! Generating your itinerary...');
    sessionStorage.removeItem('pendingTrip');
    await _doGenerate();
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      if (authMode === 'login') {
        await login(authForm.email, authForm.password);
      } else {
        await register(authForm.email, authForm.password, {
          first_name: authForm.first_name,
          last_name: authForm.last_name,
          referral_code: sessionStorage.getItem('referral_code') || '',
        });
        sessionStorage.removeItem('referral_code');
      }
      await handleAuthSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    // Save trip data before redirect
    sessionStorage.setItem('pendingTrip', JSON.stringify(formData));
    loginWithGoogle();
  };

  // On mount, check if we're returning from Google auth with a pending trip
  useEffect(() => {
    if (isAuthenticated) {
      const pending = sessionStorage.getItem('pendingTrip');
      if (pending) {
        try {
          const savedData = JSON.parse(pending);
          setFormData(savedData);
          setCurrentStep(9);
          sessionStorage.removeItem('pendingTrip');
          toast.info('Welcome! Click Generate Itinerary to continue.');
        } catch { /* silent */ }
      }
    }
  }, [isAuthenticated]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <TravelerStep formData={formData} updateFormData={updateFormData} />;
      case 2:
        return (
          <NationalityStep
            formData={formData}
            updateFormData={updateFormData}
            showOtherNationality={showOtherNationality}
            onNationalityChange={handleNationalityChange}
          />
        );
      case 3:
        return <OriginStep formData={formData} updateFormData={updateFormData} />;
      case 4:
        return (
          <DestinationStep
            formData={formData}
            setFormData={setFormData}
            updateFormData={updateFormData}
            handleCountryChange={handleCountryChange}
            removeCountry={removeCountry}
            toggleCitySelection={toggleCitySelection}
          />
        );
      case 5:
        return (
          <DateStep
            formData={formData}
            updateFormData={updateFormData}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            templateDuration={templateDuration}
          />
        );
      case 6:
        return <BudgetStep formData={formData} updateFormData={updateFormData} />;
      case 7:
        return <StyleStep formData={formData} toggleArrayItem={toggleArrayItem} />;
      case 8:
        return <InterestsStep formData={formData} toggleArrayItem={toggleArrayItem} />;
      case 9:
        return <PreferencesStep formData={formData} updateFormData={updateFormData} toggleArrayItem={toggleArrayItem} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="new-trip-page">
      {/* Vibrant Professional Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light to-sky opacity-95" />
        <div className="absolute top-20 left-10 w-96 h-96 bg-gold/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 right-20 w-80 h-80 bg-sky/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
        <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>
      
      <Header />

      <main className="pt-20 pb-10 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Hero Title */}
          <div className="text-center mb-5">
            <h1 className="font-serif text-2xl md:text-3xl text-white mb-1 drop-shadow-lg">
              {isEditMode ? 'Edit Your Trip' : 'Plan Your Dream Journey'}
            </h1>
            <p className="text-white/80 text-sm md:text-base">
              {isEditMode 
                ? 'Update your trip details and regenerate your itinerary' 
                : 'Create a personalized travel itinerary in minutes'}
            </p>
          </div>
          
          <StepProgressBar currentStep={currentStep} />

          {/* Step Content */}
          <AnimatePresence mode="wait">
            <motion.div key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-2xl p-6">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {tripReady ? (
            <div className="flex flex-col items-center gap-4 mt-5">
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-2xl p-6 w-full text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="font-serif text-xl text-ocean mb-2">Trip Details Saved!</h3>
                <p className="text-ocean/60 text-sm mb-5">Your trip to <strong>{formData.destination_city || formData.destination_cities?.[0] || 'your destination'}</strong> is ready. Generate your AI-powered itinerary now.</p>
                <div className="flex items-center justify-center gap-3">
                  <Button variant="ghost" onClick={() => setTripReady(false)} className="text-ocean border border-ocean/20 rounded-full" data-testid="edit-trip-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Edit Trip
                  </Button>
                  <Button className="bg-gold hover:bg-gold-light text-ocean font-semibold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300" onClick={handleGenerate} disabled={loading} data-testid="generate-itinerary-btn">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Generate Itinerary <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
          <div className="flex items-center justify-between mt-5">
            <Button variant="ghost" onClick={handleBack}
              className="text-white hover:bg-white/20 border border-white/30" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>

            {currentStep < 9 ? (
              <Button className="bg-gold hover:bg-gold-light text-ocean font-semibold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300" onClick={handleNext} disabled={!canProceed()} data-testid="next-btn">
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button className="bg-white hover:bg-offwhite text-ocean font-semibold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300" onClick={handleSubmit} disabled={loading} data-testid="create-trip-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  isEditMode ? <>Update Trip <ArrowRight className="w-4 h-4 ml-2" /></> : <>Create Trip <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            )}
          </div>
          )}
        </div>
      </main>

      {/* Auth Modal — shown when unauthenticated user tries to generate */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-center">
              {authMode === 'login' ? 'Sign in to generate your itinerary' : 'Quick sign up to generate your itinerary'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-gray-500 text-sm -mt-2">
            Your trip is ready! {authMode === 'register' ? 'Create a free account' : 'Sign in'} and we'll generate your personalized AI itinerary instantly.
          </p>

          <form onSubmit={handleAuthSubmit} className="space-y-3 mt-2">
            {authMode === 'register' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">First Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input value={authForm.first_name} onChange={e => setAuthForm({...authForm, first_name: e.target.value})}
                      placeholder="First name" className="pl-9 h-10" data-testid="auth-modal-firstname" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Last Name</Label>
                  <Input value={authForm.last_name} onChange={e => setAuthForm({...authForm, last_name: e.target.value})}
                    placeholder="Last name" className="h-10" data-testid="auth-modal-lastname" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="email" value={authForm.email} onChange={e => setAuthForm({...authForm, email: e.target.value})}
                  placeholder="your@email.com" className="pl-9 h-10" data-testid="auth-modal-email" required />
              </div>
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type={showPassword ? 'text' : 'password'} value={authForm.password}
                  onChange={e => setAuthForm({...authForm, password: e.target.value})}
                  placeholder="Min. 6 characters" className="pl-9 pr-10 h-10" data-testid="auth-modal-password" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={authLoading}
              className="w-full h-10 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full font-semibold"
              data-testid="auth-modal-submit">
              {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : authMode === 'login' ? 'Sign In & Generate Itinerary' : 'Sign Up & Generate Itinerary'}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <Button type="button" variant="outline" onClick={handleGoogleAuth}
            className="w-full h-10 rounded-full border-gray-300 hover:bg-gray-50 font-medium text-gray-700 gap-2"
            data-testid="auth-modal-google">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-gray-500 text-xs mt-1">
            {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button type="button" onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
              className="text-teal-700 font-medium hover:underline" data-testid="auth-modal-toggle">
              {authMode === 'login' ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewTripPage;
